// media-library-upload-zone.component.ts
// ============================================================================
// MEDIA LIBRARY UPLOAD ZONE COMPONENT
// ============================================================================
// Purpose: Handles the empty state drop zone for file uploads
// Supports drag & drop functionality and click-to-upload
// Displays image specifications and aspect ratio information
// Special Features: Avatar mode with circular design for profile pictures
// ============================================================================

import { Component, EventEmitter, Input, OnInit, Output, HostListener } from '@angular/core';
import { decimalToImageRatio, formatFileSize, UploadMetaData } from '../utils';


@Component({
  selector: 'app-media-library-upload-zone',
  templateUrl: './media-library-upload-zone.component.html',
  styleUrls: ['./media-library-upload-zone.component.css'],
})
export class MediaLibraryUploadZoneComponent implements OnInit {
  // ============================================================================
  // INPUT PROPERTIES
  // ============================================================================
  @Input() uploadTypeMeta: UploadMetaData;
  // @Input() uploadExtensions: Record<'image' | 'video' | 'file', string[]>;
  @Input() uploadType:
    | 'any'
    | 'content'
    | 'media'
    | 'video'
    | 'image'
    | 'audio' = 'media';

  /** Text to display in the upload zone */
  @Input() uploadText: string;

  /** Image specifications for validation and display hints */
  @Input() imgSpecs: {
    width?: number;
    height?: number;
    ratio?: number;
    ratioText?: string;
  };

  /** 
   * Enables avatar mode with circular design
   * When true: Changes to circular shape, optimized for profile pictures
   * Automatically sets aspect ratio to 1:1
   */
  @Input() isAvatar: boolean = false;

  /** Maximum allowed file size in bytes */
  @Input() maxFileSize: number;

  // ============================================================================
  // OUTPUT PROPERTIES - Event Emitters
  // ============================================================================

  /** Emitted when user clicks on the upload zone */
  @Output() ClickOnActiveArea: EventEmitter<any> = new EventEmitter<any>();

  /** Emitted when files are dropped onto the zone */
  @Output() FilesDropped: EventEmitter<File[]> = new EventEmitter<File[]>();

  // ============================================================================
  // PUBLIC PROPERTIES
  // ============================================================================

  /** Formatted ratio string for display (e.g., "16:9" or "1920x1080px") */
  ratioDisplay: string = '';

  /** Tracks whether user is currently dragging files over the zone */
  isDragging: boolean = false;

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor() { }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    // Calculate and format ratio display text
    if ((this.imgSpecs?.width && this.imgSpecs?.height) || this.imgSpecs?.ratio) {
      this.ratioDisplay = decimalToImageRatio(this.imgSpecs);
    } else {
      this.ratioDisplay = 'Free Ratio';
    }
  }

  // ============================================================================
  // PUBLIC METHODS - User Interaction
  // ============================================================================

  /**
   * Handles click events on the upload zone
   * Emits event to parent component to open file browser
   */
  onClickOnActiveArea(): void {
    this.ClickOnActiveArea.emit();
  }

  // ============================================================================
  // DRAG & DROP EVENT HANDLERS
  // ============================================================================

  /**
   * Handles dragover event - prevents default browser behavior
   * Sets dragging state to true for visual feedback
   */
  @HostListener('dragover', ['$event'])
  onDragOver(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.isDragging = true;
  }

  /**
   * Handles dragleave event - removes drag visual feedback
   */
  @HostListener('dragleave', ['$event'])
  onDragLeave(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.isDragging = false;
  }

  /**
   * Handles drop event - processes dropped files
   * Extracts files from event and emits them to parent
   */
  @HostListener('drop', ['$event'])
  onDrop(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    this.isDragging = false;

    // Extract files from drag event and emit to parent
    if (evt.dataTransfer?.files.length) {
      const files = Array.from(evt.dataTransfer.files);
      this.FilesDropped.emit(files);
    }
  }





  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  get formattedMaxFileSize(): string {
    return this.maxFileSize ? formatFileSize(this.maxFileSize) : '';
  }

}